import axios from 'axios';

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * True when the request failed before an HTTP status was received — typical for
 * dropped sockets (e.g. net::ERR_SOCKET_NOT_CONNECTED), timeouts, offline.
 * Not for 4xx/5xx (caller should handle auth / server errors explicitly).
 */
export function isLikelyTransientNetworkError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) {
    return false;
  }
  if (error.response != null) {
    return false;
  }
  if (error.code === 'ECONNABORTED') {
    return true;
  }
  if (error.request == null) {
    return false;
  }
  return true;
}

export type RetryTransientNetworkOptions = {
  maxAttempts?: number;
  baseDelayMs?: number;
};

/**
 * Retries an async operation when it fails with a transient transport error only.
 */
export async function withRetryOnTransientNetworkFailure<T>(
  operation: () => Promise<T>,
  options?: RetryTransientNetworkOptions
): Promise<T> {
  const maxAttempts = options?.maxAttempts ?? 5;
  const baseDelayMs = options?.baseDelayMs ?? 400;
  let lastError: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      const canRetry =
        isLikelyTransientNetworkError(error) && attempt < maxAttempts;
      if (!canRetry) {
        throw error;
      }
      await sleep(baseDelayMs * attempt);
    }
  }

  throw lastError;
}
